import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "Divisions", href: "#divisions" },
  { label: "Who We Are", href: "#who-we-are" },
  { label: "Leadership", href: "#leadership" },
  { label: "Contact", href: "#contact" },
];

export const scrollToSection = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNav = (hash) => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: hash } });
    else scrollToSection(hash);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "glass border-b border-white/10" : "bg-navy/0 border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => (location.pathname !== "/" ? navigate("/") : scrollToSection("#top"))}
          className="flex items-center gap-3"
          data-testid="nav-logo-button"
        >
          <img src="/assets/logo-light.png" alt="GENOUM GROUP" className="h-12 w-auto" />
        </button>
        <nav className="hidden md:flex items-center gap-10" data-testid="nav-links">
          {LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className="font-mono-accent text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-neon transition-colors duration-300"
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/projects"
            className="font-mono-accent text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-neon transition-colors duration-300"
            data-testid="nav-link-projects"
          >
            Projects
          </Link>
          <Link
            to="/careers"
            className="font-mono-accent text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-neon transition-colors duration-300"
            data-testid="nav-link-careers"
          >
            Careers
          </Link>
        </nav>
        <button
          onClick={() => handleNav("#contact")}
          className="hidden sm:block font-mono-accent text-xs tracking-[0.2em] uppercase bg-electric hover:bg-electric-hover text-white px-6 py-3 transition-colors duration-300"
          data-testid="nav-enquire-button"
        >
          Enquire Now
        </button>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden text-slate-300 hover:text-neon transition-colors duration-300"
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden glass border-b border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className="text-left font-mono-accent text-sm tracking-[0.2em] uppercase text-slate-300 hover:text-neon transition-colors duration-300"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </button>
              ))}
              <Link
                to="/projects"
                onClick={() => setMenuOpen(false)}
                className="font-mono-accent text-sm tracking-[0.2em] uppercase text-slate-300 hover:text-neon transition-colors duration-300"
                data-testid="mobile-nav-projects"
              >
                Projects
              </Link>
              <Link
                to="/careers"
                onClick={() => setMenuOpen(false)}
                className="font-mono-accent text-sm tracking-[0.2em] uppercase text-slate-300 hover:text-neon transition-colors duration-300"
                data-testid="mobile-nav-careers"
              >
                Careers
              </Link>
              <button
                onClick={() => handleNav("#contact")}
                className="font-mono-accent text-xs tracking-[0.2em] uppercase bg-electric text-white px-6 py-3.5 flex items-center justify-center gap-2 mt-2"
                data-testid="mobile-nav-enquire"
              >
                Enquire Now <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
